import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetchJson } from "../api";
import { t, tf } from "../i18n";
import { BugIcon, Spinner } from "./Icons";
import { useModalPresence, overlayMotionProps, dialogMotionProps } from "../utils/modalPresence";
import { useEscapeLayer, ESCAPE_PRIORITY_MODAL } from "../utils/escapeLayers";

// Тикет о баге — администратору панели (POST /api/support/tickets, список у
// администратора в настройках). Открывается из книжечки обновлений.
//
// Того же вида, что окно подтверждений (kl-confirm-*), только с полем для
// текста. Набранное, но не отправленное не теряется при закрытии: окно
// живёт в App и держит текст до отправки.
const TEXT_MIN_LENGTH = 5;
const TEXT_MAX_LENGTH = 2000;

const SupportTicketModal = ({ open, onClose, context = {}, onSent }) => {
  const presence = useModalPresence(open);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fieldRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setError("");
    const timer = setTimeout(() => fieldRef.current?.focus({ preventScroll: true }), 40);
    return () => clearTimeout(timer);
  }, [open]);

  const close = () => {
    if (!busy) onClose?.();
  };
  useEscapeLayer(open, close, ESCAPE_PRIORITY_MODAL + 5);

  const submit = async () => {
    if (busy) return;
    const body = text.trim();
    if (body.length < TEXT_MIN_LENGTH) {
      setError(t("Опишите проблему подробнее — хотя бы пару слов."));
      fieldRef.current?.focus();
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await apiFetchJson("/api/support/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: body, tab: context.tab || "", appVersion: context.appVersion || "" }),
        timeoutMs: 20000,
        retry: false
      });
      if (data?.success === false) throw new Error(data?.error || t("Не удалось отправить тикет"));
      setText("");
      onSent?.(data?.ticket?.id || null);
      onClose?.();
    } catch (err) {
      setError(String(err?.data?.error || err?.message || t("Не удалось отправить тикет")));
    } finally {
      setBusy(false);
    }
  };

  if (!presence.mounted || typeof document === "undefined") return null;
  const length = text.length;

  return createPortal(
    <div
      className="kl-confirm-overlay kl-ticket-overlay"
      {...overlayMotionProps(presence.state)}
      onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}
    >
      <div
        className="kl-confirm kl-ticket"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kl-ticket-title"
        {...dialogMotionProps(presence.state)}
      >
        <div className="kl-confirm-head">
          <span className="kl-confirm-icon" aria-hidden="true"><BugIcon size={20} /></span>
          <div className="kl-confirm-text">
            <div className="kl-confirm-title" id="kl-ticket-title">{t("Создать тикет")}</div>
            <div className="kl-confirm-message">
              {t("Опишите, что произошло: что делали, что ожидали и что случилось. Тикет придёт администратору панели.")}
            </div>
          </div>
        </div>
        <textarea
          ref={fieldRef}
          className="kl-ticket-field"
          value={text}
          maxLength={TEXT_MAX_LENGTH}
          rows={6}
          placeholder={t("Например: в «Сообщениях» не открывается диалог с покупателем, крутится загрузка")}
          aria-label={t("Текст тикета")}
          disabled={busy}
          onChange={(event) => {
            setText(event.target.value);
            if (error) setError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <div className="kl-ticket-meta">
          {error
            ? <span className="kl-ticket-error" role="alert">{error}</span>
            : <span>{t("Срочный вопрос — пишите @umbrellacontact в Telegram")}</span>}
          <span className="kl-ticket-count">{tf`${length} из ${TEXT_MAX_LENGTH}`}</span>
        </div>
        <div className="kl-confirm-actions">
          <button type="button" className="kl-confirm-btn" onClick={close} disabled={busy}>
            {t("Отмена")}
          </button>
          <button type="button" className="kl-confirm-btn is-primary" onClick={submit} disabled={busy}>
            {busy ? <Spinner size={15} /> : null}
            {t("Отправить")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SupportTicketModal;
